import { avatarColour, initialOf } from '../../lib/avatar';
import { cx } from '../../lib/cx';
import type { MentionCandidate } from './mentionsApi';
import { Bot } from 'lucide-react';
import { RUNDOWN_BOT_ID } from '@rundown/types';

/**
 * The `@` picker's dropdown. Purely presentational — `Composer.tsx` owns the
 * query, the filtering and the keyboard handling, and hands this component
 * only what to draw and where.
 *
 * Positioned `fixed` at caret coordinates from `editor.view.coordsAtPos`
 * (viewport-relative, so `fixed` needs no portal) rather than as a
 * ProseMirror decoration or a NodeView popup — those need `@tiptap/suggestion`
 * machinery this project does not have installed.
 */
export function MentionMenu({
  candidates,
  highlightedIndex,
  left,
  top,
  onSelect,
  onHover,
}: {
  candidates: readonly MentionCandidate[];
  highlightedIndex: number;
  left: number;
  top: number;
  onSelect: (candidate: MentionCandidate) => void;
  onHover: (index: number) => void;
}) {
  if (candidates.length === 0) return null;

  return (
    <div
      role="listbox"
      aria-label="Mention someone"
      style={{ left, top }}
      className="fixed z-50 w-56 overflow-hidden rounded-[10px] border border-line bg-base py-1 shadow-lg"
    >
      {candidates.map((candidate, index) => (
        <button
          key={candidate.id}
          type="button"
          role="option"
          aria-selected={index === highlightedIndex}
          // Mousedown, not click: a click fires after the editor's blur/
          // selection has already changed focus, and by then the stored
          // mention range is stale. Mousedown fires first and we prevent its
          // default so the editor never loses focus in the first place.
          onMouseDown={(event) => {
            event.preventDefault();
            onSelect(candidate);
          }}
          onMouseEnter={() => onHover(index)}
          className={cx(
            'flex w-full items-center gap-2 px-2.5 py-1.5 text-left fs-12.5',
            index === highlightedIndex ? 'bg-surface-3' : 'bg-transparent',
          )}
        >
          {candidate.id === RUNDOWN_BOT_ID ? (
            <span
              className="grid size-[1.25rem] flex-none place-items-center rounded-full bg-ink text-white"
              aria-hidden
            >
              <Bot size={11} strokeWidth={2.25} />
            </span>
          ) : (
            <span
              className={cx(
                'grid size-[1.25rem] flex-none place-items-center rounded-full fs-10 font-bold text-white',
                avatarColour(candidate.name),
              )}
              aria-hidden
            >
              {initialOf(candidate.name)}
            </span>
          )}
          <span className="min-w-0 flex-1 truncate font-medium text-ink">{candidate.name}</span>
          {candidate.id === RUNDOWN_BOT_ID && <span className="flex-none fs-10.5 text-faint">assistant</span>}
        </button>
      ))}
    </div>
  );
}
